import EngineCleanupBase from '../../../services/engines/EngineCleanupBase';
import { EngineNames } from '../../../services/wss/events/EngineNames';
import OnJournalUpdatedHandler from './handlers/onJournalUpdatedHandler';
import ComponentNotifier from '../../../services/wss/ComponentNotifier';
import templateCache from './TemplateCache';
import templateFetcher from './TemplateFetcher';

const _name = EngineNames.TemplateEngine;

class TemplateEngine extends EngineCleanupBase {
    _onJournalUpdated = new OnJournalUpdatedHandler(templateCache);

    /**
     * Bootstraps the engine — generic init only, no fetch.
     * Called once on PageContent mount.
     */
    bootup = () => {
        this.initialize(_name);
    };

    /**
     * Fetches all approved templates and fills cache. Call after auth is confirmed.
     */
    fetchAll = async () => {
        try {
            const templates = await templateFetcher.fetch();
            if (!templates) return;

            // Populate cache — addOrUpdate handles duplicates
            templateCache.addOrUpdate(templates);
        } catch (error) {
            this.logError('fetchAll', error.message);
        }
    };

    /**
     * Returns template note by rk, or null if not in cache.
     * IframeRenderer uses this to resolve the template for a data note.
     * @param {string} rk - rk of the template note (entry.tid)
     * @returns {object|null} template note or null
     */
    getTemplate = (rk) => {
        return templateCache.get(rk);
    };

    /**
     * Returns template note by format identifier, or null if not in cache.
     * Used for widget resolution — widgets are resolved by format=widget_{name}.
     * @param {string} format - e.g. 'widget_time'
     * @returns {object|null}
     */
    getTemplateByFormat = (format) => {
        return templateCache.getByFormat(format);
    };

    /**
     * Called directly from PageContentSRHandlers on OnJournalUpdated.
     * @param {object[]} data - Updated entries from BE
     */
    onJournalUpdated = (data) => {
        try {
            if (!this._onJournalUpdated.handle(data)) {
                return;
            }

            this._notify();
        } catch (error) {
            this.logError('onJournalUpdated', error.message);
        }
    };

    /**
     * Reloads all templates from BE — called on SignalR OnTemplateApproved.
     * Clears cache first to ensure stale templates are removed.
     */
    reload = async () => {
        templateCache.clear();
        await this.fetchAll();
    };

    /**
     * Clears template cache — called on onClearCache event via EngineCleanupBase.
     */
    clear = () => {
        templateCache.clear();
        super.clear();
    };

    /**
     * Returns all cached templates for a given service.
     * @param {string} service - e.g. 'TemplateDesigner.DataTemplates'
     * @returns {object[]}
     */
    getTemplatesByService = (service) => {
        try {
            if (!service) {
                this.logError('getTemplatesByService', 'service is required');
                return [];
            }
            return templateCache.getAll().filter((t) => t.service === service);
        } catch (error) {
            this.logError('getTemplatesByService', error.message);
            return [];
        }
    };

    /**
     * notify painters
     */
    _notify = () => {
        const data = {
            templates: templateCache.getAll(),
        };

        ComponentNotifier.notify(_name, 'templatesChanged', data);
    };
}

const templateEngine = new TemplateEngine();
export default templateEngine;
